import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { GaugeCircle, KeyRound, Languages } from "lucide-react";
import axios from "axios";
import KioskLayout from "@/kiosk/KioskLayout";
import { setKioskToken, getKioskToken } from "@/lib/kioskApi";
import { API } from "@/lib/api";

/**
 * Pair screen: enter the 6-digit pairing PIN + the kiosk_id (admin shares both
 * via the wizard or the Sites page). Falls back to admin re-pair flow.
 */
export default function KioskPair() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { i18n, t } = useTranslation();
  const [kioskId, setKioskId] = useState(params.get("kiosk_id") || "");
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [branding, setBranding] = useState(null);

  // If we're already paired (token in localStorage), skip to /kiosk
  useEffect(() => {
    if (getKioskToken()) {
      nav("/kiosk", { replace: true });
    }
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/kiosks/pair`, {
        kiosk_id: kioskId.trim(),
        pin: pin.trim(),
      });
      setKioskToken(data.kiosk_token);
      // fetch branding for nicer transition
      try {
        const b = await axios.get(`${API}/sites/${data.site_id}/public-branding`);
        setBranding(b.data);
      } catch {
        /* ignore */
      }
      nav("/kiosk", { replace: true });
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not pair");
    } finally {
      setLoading(false);
    }
  };

  return (
    <KioskLayout branding={branding}>
      <div className="flex-1 flex flex-col items-center justify-center" data-testid="kiosk-pair-page">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
            <GaugeCircle className="w-7 h-7 text-orange-400" />
          </div>
          <div className="text-2xl font-semibold tracking-tight">EntryBook Kiosk</div>
        </div>

        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/15 p-8 md:p-10 max-w-md w-full">
          <div className="text-xs uppercase tracking-[0.2em] text-orange-300 mb-2">{t("page_unpaired_title")}</div>
          <h1 className="text-2xl md:text-3xl font-semibold mb-3">{t("enter_pin")}</h1>
          <p className="text-sm text-white/80 mb-6">{t("page_unpaired_body")}</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs text-white/70 uppercase tracking-wider">Kiosk ID</label>
              <input
                value={kioskId}
                onChange={(e) => setKioskId(e.target.value)}
                required
                className="w-full mt-1 bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-orange-400"
                placeholder="kiosk-id-from-admin"
                data-testid="kiosk-pair-id-input"
              />
            </div>
            <div>
              <label className="text-xs text-white/70 uppercase tracking-wider">PIN</label>
              <input
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                required
                inputMode="numeric"
                maxLength={6}
                className="w-full mt-1 bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white text-center text-3xl font-bold tracking-[0.3em] placeholder-white/40 focus:outline-none focus:border-orange-400"
                placeholder="••••••"
                data-testid="kiosk-pair-pin-input"
              />
            </div>
            {err && (
              <div className="text-sm text-red-300 bg-red-500/10 border border-red-400/30 rounded-md px-3 py-2" data-testid="kiosk-pair-error">
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white px-4 py-3 font-semibold disabled:opacity-50"
              data-testid="kiosk-pair-submit"
            >
              <KeyRound className="w-4 h-4" /> {loading ? "Pairing…" : t("pair")}
            </button>
          </form>
        </div>

        <div className="mt-8 flex items-center gap-3 text-xs text-white/60">
          <Languages className="w-4 h-4" />
          <button
            onClick={() => i18n.changeLanguage("en")}
            className={`uppercase tracking-wider ${i18n.language?.startsWith("en") ? "text-orange-300" : ""}`}
            data-testid="kiosk-lang-en"
          >EN</button>
          <span>·</span>
          <button
            onClick={() => i18n.changeLanguage("de")}
            className={`uppercase tracking-wider ${i18n.language?.startsWith("de") ? "text-orange-300" : ""}`}
            data-testid="kiosk-lang-de"
          >DE</button>
        </div>
      </div>
    </KioskLayout>
  );
}
